import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot } from '@angular/router';
import * as msal from '@azure/msal-browser';
import { IFetchOptions, IHttpClientImpl } from '@pnp/common';
import { ISPConfiguration, sp } from '@pnp/sp/presets/all';
import { environment, msalConfig } from 'src/environments/environment';
import { CurrentUserService } from './services/current-user.service';
import { delay, FatalError } from './services/utils';

export let isAuthenticated = false;
export let isLoggedOut = false;

export const msalInstance = new msal.PublicClientApplication(environment.msalConfig);

let authResultPromise: Promise<msal.AuthenticationResult>;
let authGraphResultPromise: Promise<msal.AuthenticationResult>;
let isAuthenticationInProgress = false;

const serializableMsalConf : msal.Configuration = {
  auth: msalConfig.auth,
  cache: msalConfig.cache,
  system: {
    loggerOptions: {
      logLevel: msalConfig.system.loggerOptions.logLevel,
      piiLoggingEnabled: msalConfig.system.loggerOptions.piiLoggingEnabled,
    }
  }
}

function setActiveAccount(account: msal.AccountInfo) {
  msalInstance.setActiveAccount(account);
  if (account) {
    localStorage.setItem('AccessHubActiveAccount', JSON.stringify(account));
  }
  else {
    localStorage.removeItem('AccessHubActiveAccount');
  }
}

export function saveMsalConfig() {
  sessionStorage.setItem('MsalConfig', JSON.stringify(serializableMsalConf));
}

export function acquireToken(): Promise<msal.AuthenticationResult> {
  if (authResultPromise === undefined || (/* isAcquiredTokenTimedOut() && */ !isAuthenticationInProgress)) {
    authResultPromise = acquireTokenImplementation(environment.scopes);
  }
  return authResultPromise;
}

export function acquireGraphToken(): Promise<msal.AuthenticationResult> {
  if (authGraphResultPromise === undefined || (/*  isAcquiredTokenTimedOut() && */ !isAuthenticationInProgress)) {
    authGraphResultPromise = acquireTokenImplementation(["https://graph.microsoft.com/.default "]);
  }
  return authGraphResultPromise;
}

async function acquireTokenSilent(scopes: string[]) : Promise<msal.AuthenticationResult> {
  try {
    const authResult = await msalInstance.acquireTokenSilent({
      scopes: scopes,
    });
    isAuthenticated = true;
    isLoggedOut = false;
    return authResult;
  }
  catch (err) {
    const accountJson = localStorage.getItem('AccessHubActiveAccount');
    const account = accountJson && JSON.parse(accountJson) || undefined;
    const authResult = await msalInstance.ssoSilent( {
      scopes: scopes,
      account: account
    });
    setActiveAccount(authResult.account);
    isAuthenticated = true;
    isLoggedOut = false;
    return authResult;
  }
}


export async function acquireTokenPopup(scopes: string[]): Promise<msal.AuthenticationResult> {
  const authResult = await msalInstance.acquireTokenPopup({
    scopes
  });
  setActiveAccount(authResult.account);
  isAuthenticated = true;
  isLoggedOut = false;
  return authResult;
}


async function acquireTokenImplementation(scopes: string[]): Promise<msal.AuthenticationResult> {
  let authResult: msal.AuthenticationResult = null;
  isAuthenticationInProgress = true;
  try {
    authResult = await acquireTokenSilent(scopes);
  }
  catch (err) {

    try {
      authResult = await acquireTokenPopup(scopes).finally(() => {
        isAuthenticationInProgress = false;
      });
    }
    catch (err) {
      throw new FatalError('Authentication failed', err)
    }
  }
  isAuthenticationInProgress = false;
  return authResult;
}

@Injectable({providedIn: 'root'})
export class AuthenticatedGuard implements CanActivate, CanActivateChild {

  constructor(private router: Router,
              private currentUsersService: CurrentUserService) {}
  async canActivate(
    route: ActivatedRouteSnapshot, state: RouterStateSnapshot
  ): Promise<boolean> {
    try {
      await acquireTokenSilent(environment.scopes);


    }
    catch {
      const returnUrl = state.url;
      sessionStorage.setItem('returnUrl', returnUrl);
      localStorage.setItem('AccessHubApplicationVersion', environment.version);
      if (environment.authenticationInteractionMode === 'Redirect') {
        await msalInstance.loginRedirect({scopes: environment.scopes});
      }
      else {
        this.router.navigateByUrl('/account/login');
      }
      return false;
    }
    try {
      if((await this.currentUsersService.get()).IsAccessHubMember)
        return true;
      else {
        this.router.navigateByUrl('/account/access-request');
        return false;
      }
    }
    catch {
      this.router.navigateByUrl('/error');
      return false;
    }
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    return this.canActivate(childRoute, state);
  }
}

@Injectable({
  providedIn: 'root'
})
export class SpHttpClient implements IHttpClientImpl {
  async fetch(url: string, options: IFetchOptions): Promise<Response> {
    const authResult = await acquireToken();
    const headers = (options.headers || new Headers()) as Headers;
    headers.set('Authorization', 'Bearer ' + authResult.accessToken);
    try {
      const response = await window.fetch(url, {
        body: options.body,
        credentials : options.credentials,
        cache: options.cache,
        method: options.method,
        headers,
        redirect: 'manual'
      });
      if (response.redirected) {
        console.error(response);
      }
      return response;
    }
    catch (err) {
      console.error(err);
      throw err;
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class GraphHttpClient implements IHttpClientImpl {
  async fetch(url: string, options: IFetchOptions): Promise<Response> {
    const authResult = await acquireGraphToken();
    const headers = (options.headers || new Headers()) as Headers;
    headers.set('Authorization', 'Bearer ' + authResult.accessToken);
    return window.fetch(url, {
      body: options.body,
      credentials : options.credentials,
      cache: options.cache,
      method: options.method,
      headers
    });
  }
}



export function configureSp(): void {
  const spConfig: ISPConfiguration = {
    sp: {
      baseUrl: environment.siteUrl,
      fetchClientFactory: () => new SpHttpClient()
    }
  };
  sp.setup(spConfig);
}

export async function signOut(): Promise<boolean> {
  try {
    await msalInstance.logoutPopup();
  }
  catch (err) {
    console.error(err);
    return false;
  }
  try {
    await delay(100);
    await acquireTokenSilent(environment.scopes);
    return false;
  }
  catch {
    isAuthenticated = false;
    isLoggedOut = true;
    authResultPromise = undefined;
    authGraphResultPromise = undefined;
    return true;
  }
}

